import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type { AdminSmtpConfig, UpdateSmtpConfigRequest } from '@innermate/shared';
import { TestSmtpRequestSchema, UpdateSmtpConfigRequestSchema } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';

function InfoTip({ children }: { children: React.ReactNode }) {
  return (
    <span className="group relative inline-flex items-center">
      <Info className="h-3.5 w-3.5 text-foreground-muted cursor-default" />
      <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 z-50 w-72 rounded-lg bg-foreground text-background px-3 py-2 text-xs leading-relaxed shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150">
        {children}
      </span>
    </span>
  );
}

export function AdminSmtp() {
  const cfg = useQuery({
    queryKey: ['admin', 'smtp-config'],
    queryFn: () => api.adminSmtpGet(),
  });

  return (
    <PageShell
      header={
        <PageHeader
          title="SMTP 설정"
          description="email.send 툴이 사용하는 메일 발송 서버 설정. 비밀번호는 암호화되어 저장됩니다."
        />
      }
    >
      {cfg.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : cfg.error || !cfg.data ? (
        <p className="text-sm text-destructive">
          설정을 불러올 수 없습니다:{' '}
          {cfg.error instanceof ApiError ? cfg.error.message : '알 수 없는 오류'}
        </p>
      ) : (
        <div className="mx-auto max-w-2xl space-y-6">
          <SmtpConfigCard data={cfg.data} />
        </div>
      )}
    </PageShell>
  );
}

function SmtpTestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [to, setTo] = useState('');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const mut = useMutation({
    mutationFn: (email: string) => api.adminSmtpTest({ to: email }),
    onSuccess: (res) => setResult(res),
    onError: (err: unknown) =>
      setResult({
        ok: false,
        message: err instanceof ApiError ? err.message : '알 수 없는 오류',
      }),
  });

  const handleClose = () => {
    setTo('');
    setResult(null);
    onClose();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = TestSmtpRequestSchema.safeParse({ to: to.trim() });
    if (!parsed.success) {
      setResult({ ok: false, message: '유효한 이메일 주소를 입력하세요.' });
      return;
    }
    setResult(null);
    mut.mutate(to.trim());
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="연결 테스트"
      description="저장된 설정으로 테스트 메일을 발송합니다."
      size="sm"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="수신자 이메일" htmlFor="smtp-test-to">
          <Input
            id="smtp-test-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            type="email"
            placeholder="test@example.com"
            required
            autoFocus
          />
        </Field>
        {result && (
          <p className={`text-sm ${result.ok ? 'text-success' : 'text-destructive'}`}>
            {result.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleClose}>
            취소
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? '발송 중…' : '테스트 발송'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SmtpConfigCard({ data }: { data: AdminSmtpConfig }) {
  const qc = useQueryClient();
  const [testOpen, setTestOpen] = useState(false);

  const defaults =
    data.configured === true
      ? {
          host: data.host,
          port: String(data.port),
          secure: data.secure,
          username: data.username ?? '',
          password: '',
          fromEmail: data.fromEmail,
          fromName: data.fromName,
        }
      : {
          host: '',
          port: '587',
          secure: false,
          username: '',
          password: '',
          fromEmail: '',
          fromName: 'innermate',
        };

  const [host, setHost] = useState(defaults.host);
  const [port, setPort] = useState(defaults.port);
  const [secure, setSecure] = useState(defaults.secure);
  const [username, setUsername] = useState(defaults.username);
  const [password, setPassword] = useState(defaults.password);
  const [fromEmail, setFromEmail] = useState(defaults.fromEmail);
  const [fromName, setFromName] = useState(defaults.fromName);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data.configured === true) {
      setHost(data.host);
      setPort(String(data.port));
      setSecure(data.secure);
      setUsername(data.username ?? '');
      setFromEmail(data.fromEmail);
      setFromName(data.fromName);
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (body: UpdateSmtpConfigRequest) => api.adminSmtpUpdate(body),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setPassword('');
      qc.invalidateQueries({ queryKey: ['admin', 'smtp-config'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = UpdateSmtpConfigRequestSchema.safeParse({
      host: host.trim(),
      port: Number(port),
      secure,
      username: username.trim() || undefined,
      password: password || undefined,
      fromEmail: fromEmail.trim(),
      fromName: fromName.trim(),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? '입력 오류');
      return;
    }
    mut.mutate(parsed.data);
  };

  return (
    <>
      <SmtpTestModal open={testOpen} onClose={() => setTestOpen(false)} />
      <section className="bg-surface border border-border rounded-2xl p-6">
        <header className="mb-4">
          <h2 className="text-lg font-semibold">서버 설정</h2>
          {data.configured && (
            <p className="text-xs text-foreground-muted mt-1">
              최종 수정: {new Date(data.updatedAt).toLocaleString('ko-KR')}
            </p>
          )}
        </header>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="호스트" htmlFor="smtp-host">
                <Input
                  id="smtp-host"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="smtp.example.com"
                  required
                />
              </Field>
            </div>
            <div>
              <Field label="포트" htmlFor="smtp-port">
                <Input
                  id="smtp-port"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  type="number"
                  min={1}
                  max={65535}
                  required
                />
              </Field>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={secure}
              onChange={(e) => {
                setSecure(e.target.checked);
                if (e.target.checked) setPort('465');
                else setPort('587');
              }}
            />
            <span>SSL/TLS 사용 (포트 465). OFF면 STARTTLS (포트 587).</span>
          </label>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <label htmlFor="smtp-username" className="text-sm font-medium">
                사용자 이름
              </label>
              <InfoTip>
                <span className="block">
                  <strong>Gmail</strong>: Gmail 주소 입력
                </span>
                <span className="block mt-1">
                  <strong>사내 메일 서버</strong>: 인증 불필요하면 비워도 됨
                </span>
              </InfoTip>
            </div>
            <Input
              id="smtp-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="사용자 이름"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <label htmlFor="smtp-password" className="text-sm font-medium">
                비밀번호
              </label>
              <InfoTip>
                <span className="block">
                  <strong>Gmail</strong>: 일반 비밀번호 불가.
                </span>
                <span className="block">
                  구글 계정 → 보안 → 2단계 인증 → 앱 비밀번호(16자리) 발급 후 입력
                </span>
                <span className="block mt-1">
                  <strong>기타</strong>: SMTP 서버 비밀번호
                </span>
              </InfoTip>
            </div>
            <Input
              id="smtp-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              placeholder={data.configured ? '변경 시에만 입력 (비워두면 기존 유지)' : '비밀번호'}
              autoComplete="new-password"
            />
            {data.configured && (
              <p className="text-xs text-foreground-muted">비워두면 기존 비밀번호 유지</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="발신자 이메일" htmlFor="smtp-from-email">
              <Input
                id="smtp-from-email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="noreply@example.com"
                type="email"
                required
              />
            </Field>
            <Field label="발신자 이름" htmlFor="smtp-from-name">
              <Input
                id="smtp-from-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="innermate"
                required
              />
            </Field>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success">저장되었습니다.</p>}
          <div className="flex items-center justify-end gap-2">
            {data.configured && (
              <Button type="button" variant="ghost" onClick={() => setTestOpen(true)}>
                연결 테스트
              </Button>
            )}
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? '저장 중…' : '저장'}
            </Button>
          </div>
        </form>
      </section>
    </>
  );
}
